import React from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Instagram, Youtube, Mail, MapPin } from 'lucide-react';
import { Button } from '../ui/button';
import logo from '../../assets/logo.png';
import { Web } from '@mui/icons-material';
import settings from '../../constants/settings';

const Footer = () => {
  const navigate = useNavigate();

  // Function to handle FAQ link click
  const handleFaqClick = (e) => {
    e.preventDefault();
    // Navigate to home and add hash
    navigate('/#faq');
  };
  
  // Function to handle service category links
  const handleServiceCategoryClick = (e, href) => {
    e.preventDefault();
    // Navigate programmatically to ensure the component remounts
    navigate(href);
    
    // Force scroll to top of Services page after a short delay
    setTimeout(() => {
      window.scrollTo({
        top: 0,
        behavior: 'smooth'
      });
    }, 50);
  };
  // List of services
  const services = [
    { name: 'Konsultasi Beasiswa', href: '/layanan/1' },
    { name: 'Persiapan Dokumen', href: '/services?category=Dokumen', isServiceCategory: true },
    { name: 'Persiapan Tes', href: '/services?category=Bimbingan%20Belajar', isServiceCategory: true },
    { name: 'Persiapan Bahasa', href: '/services?category=Bahasa', isServiceCategory: true },
    { name: 'Jasa Pendaftaran', href: '/services?category=Pendaftaran', isServiceCategory: true },
  ];

  // Pages
  const pages = [
    { name: 'Beranda', href: '/' },
    { name: 'Tentang Kami', href: '/tentang-kami' },
    { name: 'Blog', href: '/blog' },
    { name: 'FAQ', href: '/#faq', isHashLink: true },
    { name: 'Beasiswa', href: '/beasiswa' },
  ];

  // Social media
  const socialLinks = [
    { name: 'Website', href: 'https://pilihkuliah.com', icon: <Web className="h-5 w-5" /> },
    { name: 'Instagram', href: 'https://instagram.com/pintarpilihkuliah', icon: <Instagram className="h-5 w-5" /> },
    { name: 'Youtube', href: 'https://www.youtube.com/@pintarpilihkuliah', icon: <Youtube className="h-5 w-5" /> },
  ];

  // Contact info
  const contactInfo = [
    { 
      icon: (
        <svg
          className="h-5 w-5"
          fill="currentColor"
          viewBox="0 0 24 24"
          xmlns="http://www.w3.org/2000/svg"
        >
          <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347z" />
        </svg>
      ), 
      content: 'Chat WhatsApp', 
      href: settings.wa_consultant_link 
    },
    { 
      icon: <Mail className="h-5 w-5" />, 
      content: 'info@pilihkuliah.com', 
      href: 'mailto:info@pilihkuliah.com' 
    },
    { 
      icon: <MapPin className="h-5 w-5" />, 
      content: 'Cipinang Tahfizh Center, Pulo Gadung, DKI Jakarta, Indonesia', 
      href: 'https://maps.app.goo.gl/UTKccvEZBobNKXKx7' 
    },
  ];

  return (
    <footer className="bg-dark-navy text-white pt-16">
      <div className="container mx-auto px-4">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
          {/* Company Info */}
          <div>
            <div className="mb-4">
              <div className="flex items-center mb-4">
                <img src={logo} alt="Pintar Pilih Kuliah Logo" className="h-16 bg-white/90 rounded-full p-1 mr-3" />
                <h2 className="text-xl font-bayer">#PintarPilihKuliah</h2>
              </div>
              <p className="mb-4 text-light-cream/90">
                Platform komunitas yang memudahkan alumni pesantren dan pelajar Indonesia untuk mengakses peluang pendidikan internasional.
              </p>
            </div>
            
            <div className="flex space-x-3 mb-6">
              {socialLinks.map((social, idx) => (
                <a
                  key={idx}
                  href={social.href}
                  className="bg-gray/20 hover:bg-gray/40 p-2 rounded-full transition-colors duration-300"
                  target="_blank"
                  rel="noopener noreferrer"
                  aria-label={social.name}
                >
                  {social.icon}
                </a>
              ))}
            </div>
          </div>

          {/* Services Links */}
          <div>
            <h3 className="text-lg font-bayer mb-4 text-cream">Layanan Kami</h3>
            <ul className="space-y-2">
              {services.map((service, idx) => (
                <li key={idx}>
                  {service.isServiceCategory ? (
                    <a
                      href={service.href}
                      className="text-light-cream/80 hover:text-cream transition-colors duration-300"
                      onClick={(e) => handleServiceCategoryClick(e, service.href)}
                    >
                      {service.name}
                    </a>
                  ) : (
                    <Link
                      to={service.href}
                      className="text-light-cream/80 hover:text-cream transition-colors duration-300"
                    >
                      {service.name}
                    </Link>
                  )}
                </li>
              ))}
            </ul>
          </div>

          {/* Pages Links */}
          <div>
            <h3 className="text-lg font-bayer mb-4 text-cream">Halaman</h3>
            <ul className="space-y-2">
              {pages.map((page, idx) => (
                <li key={idx}>
                  {page.isHashLink ? (
                    <a
                      href={page.href}
                      className="text-light-cream/80 hover:text-cream transition-colors duration-300"
                      onClick={handleFaqClick}
                    >
                      {page.name}
                    </a>
                  ) : (
                    <Link
                      to={page.href}
                      className="text-light-cream/80 hover:text-cream transition-colors duration-300"
                    >
                      {page.name}
                    </Link>
                  )}
                </li>
              ))}
            </ul>
          </div>

          {/* Contact */}
          <div>
            <h3 className="text-lg font-bayer mb-4 text-cream">Hubungi Kami</h3>
            <ul className="space-y-4">
              {contactInfo.map((item, idx) => (
                <li key={idx} className="flex items-start">
                  <div className="mt-1 mr-3 bg-gray/20 p-2 rounded-full">
                    {item.icon}
                  </div>
                  <a 
                    href={item.href} 
                    className="text-light-cream/80 hover:text-cream transition-colors duration-300"
                    target={item.href.startsWith('http') ? "_blank" : undefined}
                    rel={item.href.startsWith('http') ? "noopener noreferrer" : undefined}
                  >
                    {item.content}
                  </a>
                </li>
              ))}
            </ul>

            <Button variant="outline" className="mt-6 bg-gold/20 hover:bg-gold/40 text-light-cream border-gold/30 w-full font-bayer" asChild>
              <a 
                href={settings.wa_consultant_link} 
                target="_blank" 
                rel="noopener noreferrer"
                className="flex items-center justify-center gap-2"
              >
                <svg
                  className="w-4 h-4"
                  viewBox="0 0 24 24"
                  fill="currentColor"
                >
                  <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347z" />
                </svg>
                Chat Dengan Kami
              </a>
            </Button>
          </div>
        </div>

        <div className="border-t border-gray/20 mt-12 py-6 text-center text-light-cream/60 text-sm">
          <p> {new Date().getFullYear()} © #PintarPilihKuliah. Hak Cipta Dilindungi.</p>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
